import Image from '@/components/Image';
import { IStore, OrderType } from '@/types/stores';
import { PRODUCT_IMAGE_SIZE } from '@/lib/utils/command';
import { Link } from '@/i18n/routing';
import { useTranslations } from 'next-intl';
import FastIcon from '@/public/assets/Fast.svg';
import { FreeDeliveryBadge } from './FreeDeliveryBage';
import LocationIcon from '@/public/assets/location.svg';
import { Gift, Plane } from 'lucide-react';
import { FC } from 'react';
import { trackEvent } from '@/lib/amplitude';
import { AmplitudeEvents } from '@/lib/amplitude/events';
import CurrentLanguageText from '@/components/helper/CurrentLanguageText';

type Props = {
  isFilterByLocalShipping?: boolean;
  storeContent: IStore;
};

export const InstantDeliveryBadge = () => {
  const t = useTranslations('stores');

  return (
    <span className="flex h-6 items-center justify-center space-x-1 rounded-md border border-[#E7E7E8] bg-[#F3F3F3] p-2 text-black lg:p-3 rtl:space-x-reverse">
      <Image local src={FastIcon} alt="FastIcon" width={16} height={16} />
      <span className="text-[10px] font-bold">{t('Instance')}</span>
    </span>
  );
};

const StoreCard: FC<Props> = ({ storeContent, isFilterByLocalShipping }) => {
  const { englishName, id, distance, isOpen, store, orderTypes } = storeContent;
  const t = useTranslations('stores');
  const isLocalShipping = orderTypes?.includes(OrderType?.Global);
  const isStoreSupportGift = orderTypes?.includes(OrderType?.Gift);

  const renderIndustries = store.industries.slice(0, 2);

  return (
    <Link
      href={`/stores/${id}`}
      onClick={() =>
        trackEvent(AmplitudeEvents.STORE_CLICKED, {
          storeID: store.id,
          storeName: store.englishName,
          branchID: id,
          branchName: englishName,
        })
      }
    >
      <figure className="shadow-pik flex w-full gap-3 rounded-lg bg-white p-3 px-1 duration-300 hover:translate-y-[-6px] md:px-3 lg:gap-5">
        <div className="aspect-square h-[80px] w-[80px] shrink-0 overflow-hidden rounded-lg text-lg font-bold shadow-xs lg:h-[100px] lg:w-[100px]">
          <Image
            src={store.logoURL}
            size={PRODUCT_IMAGE_SIZE.MEDIUM}
            alt={englishName}
            width={100}
            height={100}
            className="h-full w-full object-cover"
          />
        </div>

        <figcaption className="flex w-[calc(100%_-_80px)] flex-col items-start justify-center gap-1 text-sm lg:w-[calc(100%_-_100px)] lg:gap-2">
          {/* heading */}
          <div className="flex w-full flex-wrap items-center justify-between">
            <h2 className="text-sm font-bold text-[#272728] md:text-base">
              <CurrentLanguageText arabicName={store.arabicName} englishName={store.englishName} />
            </h2>

            {/* delivery */}
          </div>

          {/* sub heading store name and destaince  */}
          <div className="flex w-full items-center">
            {/* is open */}
            {/* <span
              className={` ${isOpen ? 'bg-green-600' : 'bg-red-600'} inline-block h-[11px] w-[11px] rounded-full`}
            />

            <h3 className="mx-w-[55%] truncate text-[13px] font-bold text-[#272728] lg:text-[13px]">
              {CurrentLanguageText(arabicName, englishName)}
            </h3> */}

            {/* start the industries  */}
            {renderIndustries.map((item, index) => (
              <p
                className="text-gray m-0 flex items-center p-0 text-xs font-semibold"
                key={item.id}
              >
                <span>
                  <CurrentLanguageText
                    arabicName={item.arabicName}
                    englishName={item.englishName}
                  />
                </span>
                {index < renderIndustries.length - 1 && (
                  <span className="bg-gray mx-1 inline-block h-1 w-1 rounded-full" />
                )}
              </p>
            ))}
          </div>

          {/* slogan  will comment untill there is a slogn */}

          {/* <p className="lg:text-xs text-[#868687] text-[10px]">
          Slogan slogan slogan slogan slogan.
        </p> */}

          <div className="flex w-full gap-2">
            {isLocalShipping && isFilterByLocalShipping ? (
              <div className="flex items-center gap-1 rounded-md border border-[#076FCE] bg-[#076ece0d] px-1 text-sm text-[#076FCE]">
                <Plane width={16} height={16} /> {t('LocalShipping')}
              </div>
            ) : (
              <div className="flex w-full items-center justify-between">
                <div className="item center flex gap-1">
                  {/* free delivery  */}
                  <FreeDeliveryBadge />
                  {/* instant delivery  */}
                  {isOpen ? <InstantDeliveryBadge /> : null}
                  {/* store gift  */}
                  {isStoreSupportGift && (
                    <div className="flex h-6 items-center justify-center gap-1 rounded-md border border-[#E7E7E8] bg-[#F3F3F3] p-2 text-[10px] font-semibold text-nowrap text-black lg:p-3 rtl:space-x-reverse">
                      <Gift width={16} height={16} />
                      {/* {t('GiftStore')} */}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center justify-center space-x-1 text-[10px] text-[#868687] lg:text-xs rtl:space-x-reverse">
                  <Image
                    local
                    src={LocationIcon}
                    width={14}
                    height={14}
                    alt="location"
                    className="h-[14px] w-[14px]"
                  />

                  <span className="text-primary/50">
                    {distance} {t('km')}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* <div className="mt-0 self-end px-2 text-[14px] font-bold text-primary hover:underline lg:mt-4 lg:text-sm">
            {t('view-store')}
          </div> */}
        </figcaption>
      </figure>
    </Link>
  );
};

export default StoreCard;
